// HorizontalButtonGroup.jsx
import { useState, useRef, useEffect } from 'react';
import './HorizontalButtonGroup.css';
import { Button } from 'react-bootstrap';
import cn from 'classnames';

const HorizontalButtonGroup = ({ 
  buttons = [],
  defaultActive = null,
  onButtonClick = () => {},
  scrollAmount = 200,
  className = '',
  ariaLabel = {
    scrollLeft: 'Scroll left',
    scrollRight: 'Scroll right'
  }
}) => {
  const [activeButton, setActiveButton] = useState(defaultActive || buttons[0]);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef(null);

  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName);
    onButtonClick(buttonName);
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (container) {
      // Check if content overflows the container
      const contentOverflows = container.scrollWidth > container.clientWidth;
      setHasOverflow(contentOverflows);
      
      if (contentOverflows) {
        const isAtStart = container.scrollLeft <= 10;
        const isAtEnd = container.scrollLeft >= container.scrollWidth - container.clientWidth - 10;
        
        setShowLeftArrow(!isAtStart);
        setShowRightArrow(!isAtEnd);
      } else {
        // No overflow, hide both arrows
        setShowLeftArrow(false);
        setShowRightArrow(false);
      }
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Initial check
      checkScrollPosition();
      
      // Listen to scroll events
      container.addEventListener('scroll', checkScrollPosition);
      
      // Listen to resize events
      const handleResize = () => setTimeout(checkScrollPosition, 100);
      window.addEventListener('resize', handleResize);
      
      // Use ResizeObserver to detect when container size changes
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(checkScrollPosition, 50);
      });
      resizeObserver.observe(container);
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
      };
    }
  }, [buttons]);

  if (!buttons || buttons.length === 0) {
    return null;
  }

  return (
    <div className={`horizontal-button-group ${className}`}>
      <div className="scroll-wrapper">
        {/* Left Arrow */}
        {hasOverflow && <button 
          className={`scroll-arrow ${!showLeftArrow ? 'disabled' : ''}`}
          onClick={scrollLeft}
          disabled={!showLeftArrow}
          aria-label={ariaLabel.scrollLeft}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
        }

        {/* Scrollable Button Container */}
        <div 
          ref={scrollContainerRef}
          className={cn("scroll-container group-buttons buttons", {hasOverflow: hasOverflow})}
        >
          {buttons.map((button, index) => (
            <>
              <Button
                variant="outline-primary"
                key={typeof button === 'string' ? button : button.id || index}
                className={`${
                  activeButton === (typeof button === 'string' ? button : button.value) ? 'active' : ''
                }`}
                onClick={() => handleButtonClick(typeof button === 'string' ? button : button.value)}
              >
                {typeof button === 'string' ? button : button.label}
              </Button>
              {index < buttons.length - 1 && <div className="button-separator" key={`sep-${index}`}>&nbsp;</div>}
            </>
          ))}
        </div>

        {/* Right Arrow */}
        {hasOverflow && <button 
          className={`scroll-arrow ${!showRightArrow ? 'disabled' : ''}`}
          onClick={scrollRight}
          disabled={!showRightArrow}
          aria-label={ariaLabel.scrollRight}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
        }
      </div>
    </div>
  );
};

export default HorizontalButtonGroup;